import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import {
    faCircle,
    faAngleDown,
    faCheck,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import Email from "./ModalEmail";
import axios from "axios";
import { getStoredMembers, setStoredTeamActions } from "../utils/storage";
import { generateUniqId13 } from "../utils/generateUniqId13";

const DEFAULT_CATEGORIES = [
    { category_id: 1, category_name: "General Risk", color: "#FF5A00" },
    { category_id: 2, category_name: "Sector A General", color: "#DF1995" },
    { category_id: 3, category_name: "Data Privacy", color: "#8031A7" },
    { category_id: 4, category_name: "Cyber Security", color: "#00A3E0" },
    { category_id: 5, category_name: "Operational Risk", color: "#00BFB3" },
];

const ActionItem = (
    { teamAction, deleteTeamAction, updateTeamAction, clearSession },
    ref,
) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [title, setTitle] = useState("");
    const textareaRef = useRef(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isOpenEmail, setOpenEmail] = useState(false);
    const [showPlayAgainWarning, setShowPlayAgainWarning] = useState(false);
    const [activeAssignIndex, setActiveAssignIndex] = useState(null);
    const [assignMemberInput, setAssignMemberInput] = useState("");
    const [availableMembers, setAvailableMembers] = useState([]);

    const handleOpenPlayAgainWarning = () => {
        setShowPlayAgainWarning(true);
    };

    const handleCancelPlayAgain = () => {
        setShowPlayAgainWarning(false);
    };

    const handleConfirmPlayAgain = () => {
        setShowPlayAgainWarning(false);
        if (clearSession) {
            clearSession();
        } else {
            navigate(-1);
        }
    };

    const header_text =
        window.team_action?.team_action_header_text || "Team actions";
    const navigate = useNavigate();
    const useWp = window.end_page?.end_send_email_via_wp;

    const categoriesList =
        window.categories && window.categories.length > 0
            ? window.categories
            : DEFAULT_CATEGORIES;

    useEffect(() => {
        const stored = getStoredMembers([]);
        if (Array.isArray(stored)) {
            const names = stored
                .map((m) =>
                    typeof m === "object" ? m.name || m.playerName : m,
                )
                .filter((n) => typeof n === "string" && n.trim().length > 0);
            setAvailableMembers(Array.from(new Set(names)));
        }
    }, [activeAssignIndex]);

    const handleOpenAssign = (index) => {
        if (activeAssignIndex === index) {
            setActiveAssignIndex(null);
            setAssignMemberInput("");
        } else {
            setActiveAssignIndex(index);
            setAssignMemberInput("");
        }
    };

    const handleCloseAssign = (e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        setActiveAssignIndex(null);
        setAssignMemberInput("");
    };

    const handleSelectMember = (index, memberName) => {
        if (!memberName || memberName.trim().length === 0) return;
        const trimmed = memberName.trim();
        const currentItem = items[index];
        const isObject =
            typeof currentItem === "object" && currentItem !== null;
        const updatedItem = isObject
            ? { ...currentItem, playerName: trimmed }
            : { text: currentItem, playerName: trimmed, categoryName: null };

        if (typeof updateTeamAction === "function") {
            updateTeamAction(updatedItem, index);
        }

        const newItems = [...items];
        newItems[index] = updatedItem;
        setItems(newItems);
        setStoredTeamActions(newItems);

        setActiveAssignIndex(null);
        setAssignMemberInput("");
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(
                textareaRef.current.scrollHeight,
                180,
            )}px`;
        }
    }, [title]);

    const handleChange = (e) => {
        setTitle(e.target.value);
    };

    const isAddDisabled = title.trim().length === 0 || !selectedCategory;

    const handleSubmit = () => {
        if (!isAddDisabled) {
            const actionId = generateUniqId13();
            updateTeamAction({
                text: title,
                categoryId: selectedCategory.category_id,
                categoryName: selectedCategory.category_name,
                categoryColor: selectedCategory.color || "#00A3E0",
                playerName: "Assign to member",
                actionId: actionId,
            });
            setTitle("");
            setSelectedCategory(null);
            setIsDropdownOpen(false);
            if (textareaRef.current) {
                textareaRef.current.style.height = "auto";
            }
        }
    };

    const handleEmail = () => {
        const email = "";
        const mailTitle = window.end_page?.end_page_title || "Team Actions";
        const subject = encodeURIComponent(
            window.end_page?.end_page_email_subject || "Team Actions",
        );
        const message = window.end_page?.end_page_email_body || "";

        const body = `${message}\n\n${teamAction
            .map((action) => {
                if (typeof action === "object" && action !== null) {
                    const player = action.playerName
                        ? `[${action.playerName}] `
                        : "[Assign to member] ";
                    const cat = action.categoryName
                        ? `[${action.categoryName}] `
                        : "";
                    return `- ${player}${cat}${action.text}\n\n`;
                }
                return `- [Assign to member] ${action}\n\n`;
            })
            .join("")}`;

        window.open(
            `https://mail.google.com/mail/?view=cm&to=${email}&su=${subject}&body=${encodeURIComponent(
                body,
            )}`,
            "_blank",
        );
    };

    const openEmailPopup = () => {
        setOpenEmail(true);
    };

    const closeEmailPopup = () => {
        setOpenEmail(false);
    };

    useEffect(() => {
        setItems(teamAction || []);
    }, [teamAction]);

    const addActionText =
        window.end_page?.end_custom_team_action_add_button || "Toevoegen";
    const popUpSendEmailText = window.end_page?.end_send_email_end_button;
    const inputTitle =
        window.end_page?.end_page_team_action_input_title || "Add Action";
    const inputPlaceholder =
        window.end_page?.end_page_team_action_input_placeholder ||
        "Add here an agreement you made with your team";

    return (
        <>
            <div className="action-wrapper">
                {/* HEADER TITLE */}
                <h2 className="action-header-title">{header_text}</h2>

                {/* INPUT BOX AT THE TOP (FIGMA 6106:13725) */}
                <form
                    className="add-action-card"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <textarea
                        ref={textareaRef}
                        className="add-action-input"
                        value={title}
                        placeholder={inputPlaceholder}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                        rows={1}
                    />

                    <div className="add-action-bar">
                        {/* CATEGORY SELECTOR DROPDOWN */}
                        <div className="category-select-container">
                            <button
                                type="button"
                                className="category-select-button"
                                onClick={() =>
                                    setIsDropdownOpen(!isDropdownOpen)
                                }
                            >
                                <span
                                    className="selected-category-label"
                                    style={
                                        selectedCategory
                                            ? {
                                                  color: selectedCategory.color,
                                              }
                                            : {}
                                    }
                                >
                                    {selectedCategory
                                        ? selectedCategory.category_name
                                        : "Select Category"}
                                </span>
                                <FontAwesomeIcon
                                    icon={faAngleDown}
                                    className="dropdown-arrow-icon"
                                />
                            </button>

                            {isDropdownOpen && (
                                <div className="category-dropdown-menu">
                                    <div
                                        className="category-dropdown-item reset"
                                        onClick={() => {
                                            setSelectedCategory(null);
                                            setIsDropdownOpen(false);
                                        }}
                                    >
                                        <span>Select Category (None)</span>
                                    </div>
                                    {categoriesList.map((cat, idx) => (
                                        <div
                                            key={cat.category_id || idx}
                                            className={`category-dropdown-item ${selectedCategory?.category_name === cat.category_name ? "active" : ""}`}
                                            onClick={() => {
                                                setSelectedCategory(cat);
                                                setIsDropdownOpen(false);
                                            }}
                                        >
                                            <span
                                                className="category-color-dot"
                                                style={{
                                                    backgroundColor:
                                                        cat.color || "#00A3E0",
                                                }}
                                            />
                                            <span>{cat.category_name}</span>
                                            {selectedCategory?.category_name ===
                                                cat.category_name && (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="check-icon"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            className="add-action-button"
                            type="button"
                            disabled={isAddDisabled}
                            onClick={handleSubmit}
                        >
                            {addActionText}
                        </button>
                    </div>
                </form>

                {/* MAXIMIZED ACTION ITEMS LIST */}
                <ul className="action">
                    {items.map((item, index) => {
                        const isObject =
                            typeof item === "object" && item !== null;
                        const itemText = isObject ? item.text : item;
                        const categoryName = isObject
                            ? item.categoryName
                            : null;
                        const categoryColor = isObject
                            ? item.categoryColor
                            : null;
                        const rawPlayerName = isObject ? item.playerName : null;
                        const isUnassigned =
                            !rawPlayerName ||
                            rawPlayerName === "Assign to member";
                        const playerName = isUnassigned
                            ? "Assign to member"
                            : rawPlayerName;

                        const itemVariantClass = isUnassigned
                            ? "action-item unassigned"
                            : "action-item has-member";

                        const isAssigningThis =
                            isUnassigned && activeAssignIndex === index;

                        return (
                            <React.Fragment key={index}>
                                <li className={itemVariantClass}>
                                    <div
                                        className="content-text"
                                        onClick={
                                            isUnassigned
                                                ? () => handleOpenAssign(index)
                                                : undefined
                                        }
                                        title={
                                            isUnassigned
                                                ? "Click to assign member"
                                                : undefined
                                        }
                                    >
                                        <div className="text-body-container">
                                            <div className="action-tags-row">
                                                {isUnassigned ? (
                                                    <span className="action-player-name unassigned">
                                                        <FontAwesomeIcon
                                                            icon={faUser}
                                                            className="user-icon"
                                                        />
                                                        <span className="assign-text">
                                                            {playerName}
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="action-player-name">
                                                        {playerName}
                                                    </span>
                                                )}
                                                {playerName && categoryName && (
                                                    <span className="action-tag-dot" />
                                                )}
                                                {categoryName && (
                                                    <span className="action-category-tag">
                                                        {categoryName}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="item-text">
                                                {itemText}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className="delete-action"
                                        onClick={() => deleteTeamAction(item)}
                                        title="Delete"
                                    >
                                        <FontAwesomeIcon
                                            className="trash-icon"
                                            icon={faTrash}
                                        />
                                    </button>
                                </li>

                                {/* FIGMA VARIANT 3 (6106:15363): INLINE ASSIGNATION INPUT & DROPDOWN */}
                                {isAssigningThis && (
                                    <li className="action-item action-assign-box">
                                        <div className="content-text assign-content">
                                            <div className="text-body-container">
                                                <div className="action-tags-row assign-label-row">
                                                    <FontAwesomeIcon
                                                        icon={faUser}
                                                        className="user-icon assign-user-icon"
                                                    />
                                                    <span className="assign-text assign-label-text">
                                                        Assign to:
                                                    </span>
                                                </div>
                                                <div className="assign-input-container">
                                                    <input
                                                        type="text"
                                                        className="assign-input-field"
                                                        placeholder="Member name"
                                                        value={
                                                            assignMemberInput
                                                        }
                                                        onChange={(e) =>
                                                            setAssignMemberInput(
                                                                e.target.value,
                                                            )
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key ===
                                                                "Enter"
                                                            ) {
                                                                e.preventDefault();
                                                                handleSelectMember(
                                                                    index,
                                                                    assignMemberInput,
                                                                );
                                                            } else if (
                                                                e.key ===
                                                                "Escape"
                                                            ) {
                                                                handleCloseAssign();
                                                            }
                                                        }}
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>

                                            {/* POPUP LIST DROPDOWN OF AVAILABLE MEMBERS */}
                                            {availableMembers.length > 0 && (
                                                <div className="assign-members-dropdown">
                                                    {availableMembers
                                                        .filter((m) =>
                                                            assignMemberInput
                                                                ? m
                                                                      .toLowerCase()
                                                                      .includes(
                                                                          assignMemberInput.toLowerCase(),
                                                                      )
                                                                : true,
                                                        )
                                                        .map(
                                                            (
                                                                memberName,
                                                                mIdx,
                                                            ) => (
                                                                <div
                                                                    key={mIdx}
                                                                    className="assign-member-item"
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleSelectMember(
                                                                            index,
                                                                            memberName,
                                                                        );
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faUser
                                                                        }
                                                                        className="member-user-icon"
                                                                    />
                                                                    <span>
                                                                        {
                                                                            memberName
                                                                        }
                                                                    </span>
                                                                </div>
                                                            ),
                                                        )}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="delete-action close-assign-action"
                                            onClick={handleCloseAssign}
                                            title="Close"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                className="times-icon"
                                            />
                                        </button>
                                    </li>
                                )}
                            </React.Fragment>
                        );
                    })}
                </ul>

                {/* FOOTER BUTTONS MATCHING FIGMA 6106:13771 */}
                <div className="btn-action-container">
                    <Button
                        className="btn-action send-email-btn"
                        iconPosition="start"
                        color="#10069F"
                        background="#FDD100"
                        text={
                            window.end_page?.end_send_email_button
                                ? window.end_page.end_send_email_button
                                : t("Send to Email")
                        }
                        onClick={useWp ? openEmailPopup : handleEmail}
                    />
                    <Button
                        onClick={handleOpenPlayAgainWarning}
                        className="btn-action play-again-btn"
                        iconPosition="start"
                        color="white"
                        background="rgba(255, 255, 255, 0.05)"
                        text={
                            window.end_page?.end_play_again_button
                                ? window.end_page.end_play_again_button
                                : t("Play Again")
                        }
                    />
                </div>
            </div>
            <Email
                isOpen={isOpenEmail}
                onClose={closeEmailPopup}
                emailButton={{
                    text: popUpSendEmailText,
                    color: window.button_color?.text_login_color,
                    background: window.themes?.primary_color,
                }}
                teamAction={teamAction}
            />

            {showPlayAgainWarning && (
                <div className="restart-session-modal-overlay">
                    <div className="restart-session-modal">
                        <h3 className="restart-session-modal__title">
                            {window.end_page?.play_again_popup?.title ||
                                t("Play Again?")}
                        </h3>
                        <p className="restart-session-modal__desc">
                            {window.end_page?.play_again_popup?.subtitle ||
                                t(
                                    "Are you sure you want to play again? This will reset the current game session.",
                                )}
                        </p>
                        <div className="restart-session-modal__actions">
                            <button
                                type="button"
                                className="restart-session-modal__btn restart-session-modal__btn--cancel"
                                onClick={handleCancelPlayAgain}
                            >
                                {window.end_page?.play_again_popup
                                    ?.cancel_button_text || t("Cancel")}
                            </button>
                            <button
                                type="button"
                                className="restart-session-modal__btn restart-session-modal__btn--confirm"
                                onClick={handleConfirmPlayAgain}
                            >
                                {window.end_page?.play_again_popup
                                    ?.play_button_text || t("Play Again")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ActionItem;
